import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { setUnauthorizedHandler } from '../api/client';
import * as authApi from '../api/endpoints/auth';
import type { WhatsAppLink } from '../api/endpoints/auth';
import type { LoginResponse } from '../api/schemas';
import {
  clearSession,
  getSession,
  markCardConfirmed,
  markPasswordChanged,
  restoreSession,
  setSession,
  setSessionBusinessId,
  type StoredSession,
} from './session-store';

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type SessionContextValue = {
  status: SessionStatus;
  session: StoredSession | null;
  login: (
    identifier: string,
    password: string,
    deviceId?: string,
    whatsappLink?: WhatsAppLink,
  ) => Promise<LoginResponse>;
  /**
   * Takes a session another way in already opened (Entrar con WhatsApp), the
   * same as a password login would. Optional only so session doubles written
   * before WhatsApp still fit.
   */
  adoptLogin?: (result: LoginResponse) => void;
  changePassword: (currentPassword: string, newPassword: string, idempotencyKey: string) => Promise<void>;
  logout: () => Promise<void>;
  /** After a successful location switch: moves the session's businessId, nothing else. */
  setActiveBusinessId: (businessId: string) => void;
  /**
   * Stripe confirmed the card: the panel stops sending the owner to the card
   * step. Optional only so session doubles written before Stripe still fit.
   */
  confirmCard?: () => void;
};

export const SessionContext = createContext<SessionContextValue | null>(null);

type SessionProviderProps = {
  children: ReactNode;
  /** Called whenever a request comes back 401, after the local session is cleared. */
  onUnauthorized: () => void;
};

export function SessionProvider({ children, onUnauthorized }: SessionProviderProps) {
  const [session, setSessionState] = useState<StoredSession | null>(() => getSession());
  const [status, setStatus] = useState<SessionStatus>('loading');

  useEffect(() => {
    const restored = restoreSession();
    setSessionState(restored);
    setStatus(restored ? 'authenticated' : 'unauthenticated');
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSessionState(null);
      setStatus('unauthenticated');
      onUnauthorized();
    });
    return () => setUnauthorizedHandler(null);
  }, [onUnauthorized]);

  const adoptLogin = useCallback((result: LoginResponse) => {
    const next = toStoredSession(result);
    setSession(next);
    setSessionState(next);
    setStatus('authenticated');
  }, []);

  const login = useCallback(
    async (identifier: string, password: string, deviceId?: string, whatsappLink?: WhatsAppLink) => {
      const result = await authApi.login(identifier, password, deviceId, whatsappLink);
      adoptLogin(result);
      return result;
    },
    [adoptLogin],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string, idempotencyKey: string) => {
      await authApi.changePassword(currentPassword, newPassword, idempotencyKey);
      const next = markPasswordChanged();
      if (next) setSessionState(next);
    },
    [],
  );

  const setActiveBusinessId = useCallback((businessId: string) => {
    const next = setSessionBusinessId(businessId);
    if (next) setSessionState(next);
  }, []);

  const confirmCard = useCallback(() => {
    const next = markCardConfirmed();
    if (next) setSessionState(next);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearSession();
      setSessionState(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, session, login, adoptLogin, changePassword, logout, setActiveBusinessId, confirmCard }),
    [status, session, login, adoptLogin, changePassword, logout, setActiveBusinessId, confirmCard],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The record a login body leaves behind, whichever way in opened it. */
function toStoredSession(result: LoginResponse): StoredSession {
  return {
    transport: result.sessionTransport,
    businessUserId: result.businessUserId,
    businessId: result.businessId,
    role: result.role,
    staffMemberId: result.staffMemberId ?? null,
    expiresAt: result.expiresAt,
    mustChangePassword: result.mustChangePassword,
    cardRequired: result.cardRequired ?? false,
    trialEndsAt: result.trialEndsAt ?? null,
    ...(result.sessionTransport === 'bearer' && result.sessionToken ? { token: result.sessionToken } : {}),
  };
}
